import type { I18nLocale } from "@/modules/i18n/types"
import type { ChangeEvent, Dispatch, SetStateAction } from "react"

import { LOCAL_STORAGE_FORM_DATA_KEY } from "@/modules/corporate/constants"

export const LANGUAGE_VALUES: Partial<Record<I18nLocale, string>> = {
  de: "German",
  en: "English",
  fr: "French",
}

export const DIAL_CODES = [
  // Available locales
  { code: "FR", dial_code: "+33", flag: "🇫🇷", name: "France" },
  { code: "GB", dial_code: "+44", flag: "🇬🇧", name: "United Kingdom", preferred: true },
  { code: "DE", dial_code: "+49", flag: "🇩🇪", name: "Germany" },
  // Others
  { code: "CA", dial_code: "+1", flag: "🇨🇦", name: "Canada" },
  { code: "US", dial_code: "+1", flag: "🇺🇸", name: "United States", preferred: true },
  { code: "RU", dial_code: "+7", flag: "🇷🇺", name: "Russia" },
  { code: "GR", dial_code: "+30", flag: "🇬🇷", name: "Greece" },
  { code: "NL", dial_code: "+31", flag: "🇳🇱", name: "Netherlands" },
  { code: "BE", dial_code: "+32", flag: "🇧🇪", name: "Belgium" },
  { code: "ES", dial_code: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "HU", dial_code: "+36", flag: "🇭🇺", name: "Hungary" },
  { code: "IT", dial_code: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "RO", dial_code: "+40", flag: "🇷🇴", name: "Romania" },
  { code: "CH", dial_code: "+41", flag: "🇨🇭", name: "Switzerland" },
  { code: "AT", dial_code: "+43", flag: "🇦🇹", name: "Austria" },
  { code: "DK", dial_code: "+45", flag: "🇩🇰", name: "Denmark" },
  { code: "SE", dial_code: "+46", flag: "🇸🇪", name: "Sweden" },
  { code: "NO", dial_code: "+47", flag: "🇳🇴", name: "Norway" },
  { code: "PL", dial_code: "+48", flag: "🇵🇱", name: "Poland" },
  { code: "AU", dial_code: "+61", flag: "🇦🇺", name: "Australia", preferred: true },
  { code: "CN", dial_code: "+86", flag: "🇨🇳", name: "China" },
  { code: "MU", dial_code: "+230", flag: "🇲🇺", name: "Mauritius" },
  { code: "YT", dial_code: "+262", flag: "🇾🇹", name: "Mayotte" },
  { code: "PT", dial_code: "+351", flag: "🇵🇹", name: "Portugal" },
  { code: "LU", dial_code: "+352", flag: "🇱🇺", name: "Luxembourg" },
  { code: "IE", dial_code: "+353", flag: "🇮🇪", name: "Ireland" },
  { code: "IS", dial_code: "+354", flag: "🇮🇸", name: "Iceland" },
  { code: "MT", dial_code: "+356", flag: "🇲🇹", name: "Malta" },
  { code: "CY", dial_code: "+357", flag: "🇨🇾", name: "Cyprus" },
  { code: "FI", dial_code: "+358", flag: "🇫🇮", name: "Finland" },
  { code: "BG", dial_code: "+359", flag: "🇧🇬", name: "Bulgaria" },
  { code: "LT", dial_code: "+370", flag: "🇱🇹", name: "Lithuania" },
  { code: "EE", dial_code: "+372", flag: "🇪🇪", name: "Estonia" },
  { code: "MC", dial_code: "+377", flag: "🇲🇨", name: "Monaco" },
  { code: "UA", dial_code: "+380", flag: "🇺🇦", name: "Ukraine" },
  { code: "HR", dial_code: "+385", flag: "🇭🇷", name: "Croatia" },
  { code: "SI", dial_code: "+386", flag: "🇸🇮", name: "Slovenia" },
  { code: "CZ", dial_code: "+420", flag: "🇨🇿", name: "Czech Republic" },
  { code: "GE", dial_code: "+995", flag: "🇬🇪", name: "Georgia" },
]

export const DIAL_CODE_OPTIONS = DIAL_CODES.map(({ dial_code: dialCode, flag, name }) => ({
  label: `${flag} ${dialCode}`,
  value: name,
}))

export function cleanLocalStorage(): void {
  window.localStorage.removeItem(LOCAL_STORAGE_FORM_DATA_KEY)
}

export function getOptionFromValue(
  options: { label: string; value: string }[],
  value?: string
): { label: string; value: string } | null {
  return value ? options.find(option => option.value === value) || null : null
}

export function handleInputFieldChange<FieldId extends string>({
  event,
  formState,
  formUpdater,
}: {
  event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  formState: Partial<Record<FieldId, string>>
  formUpdater: Dispatch<SetStateAction<Partial<Record<FieldId, string>>>>
}): void {
  if (event.target && "id" in event.target && "value" in event.target) {
    formUpdater({
      ...formState,
      [`${event.target.id}`]: event.target.value,
    })
  }
}

export function handleSelectFieldChange<FieldId extends string>({
  name,
  data,
  formState,
  formUpdater,
}: {
  name: FieldId
  data: { label: string; value: string } | null
  formState: Partial<Record<FieldId, string>>
  formUpdater: Dispatch<SetStateAction<Partial<Record<FieldId, string>>>>
}): void {
  formUpdater({
    ...formState,
    [name]: data ? data.value : "",
  })
}
